import { IAttribute, IModel, IViolation } from '../base/annotation';
import { Metamodel } from '../base/model';
import { Activity } from './activity';
import { format } from '../../shared/utils/string.util';
import { ParameterValue } from '../base/parameter-value';
import { shellStringResources } from '../../Resources/ShellStringResources';
import { Office365EmailLinkedService } from '../linkedservice/office365-email.linkedservice';
import { MicrosoftTeamsLinkedService } from '../linkedservice/microsoft-teams.linkedservice';
import { PostInType } from './microsoft-teams.activity';
import { LinkedService } from '../linkedservice/linkedservice';

export enum ApprovalType { 
    Office365 = 'Office365', 
    MicrosoftTeams = 'MicrosoftTeams', 
    CustomEndpoint = 'CustomEndpoint' 
}

export enum OperationType {
    ApprovalMail = 'ApprovalMail',
    ApprovalCustomHook = 'ApprovalCustomHook',
    ApprovalGroupChatMessage = 'ApprovalGroupChatMessage',
    ApprovalChannelMessage = 'ApprovalChannelMessage'
}

@Metamodel.model({
    name: 'Approval',
    referable: true
})
export class FabricApprovalActivity extends Activity {
    public static readonly MODEL: IModel;
    public static readonly APPROVERS: IAttribute;
    public static readonly APPROVAL_REQUEST_TITLE: IAttribute;
    public static readonly DESCRIPTION: IAttribute;
    public static readonly LINKED_SERVICE_PARAMETERS: IAttribute;
    public static readonly BODY: IAttribute;
    public static readonly CHATID: IAttribute;
    public static readonly TEAMID: IAttribute;
    public static readonly CHANNELID: IAttribute;
    public static readonly UPN: IAttribute;

    @Metamodel.attribute({
        association: 'LINKED_SERVICE_PARAMETERS'
    })
    public linkedServiceParameters?: ParameterValue[] = [];

    @Metamodel.attribute({
        display: shellStringResources.ApproverLabel,
        association: 'APPROVERS',
        constraint: {
            validator: FabricApprovalActivity.validateApprovers
        },
        templatable: true,
        accepts: ['String']
    })
    public approvers?: string;

    @Metamodel.attribute({
        display: shellStringResources.RequestLabel,
        association: 'APPROVAL_REQUEST_TITLE',
        constraint: {
            validator: FabricApprovalActivity.validateApprovalRequestTitle
        },
        templatable: true,
        accepts: ['String']
    })
    public approvalRequestTitle?: string;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_Description,
        association: 'DESCRIPTION',
        templatable: true,
        accepts: ['String']
    })
    public approvalDescription?: string;

    @Metamodel.attribute({
        display: shellStringResources.ConnectionLabel,
        reference: true,
        type: Office365EmailLinkedService.MODEL,
        constraint: {
            validator: FabricApprovalActivity.validateEmailConnection
        }
    })
    public emailConnection?: Office365EmailLinkedService;

    @Metamodel.attribute({
        display: shellStringResources.ConnectionLabel,
        reference: true,
        type: MicrosoftTeamsLinkedService.MODEL,
        constraint: {
            validator: FabricApprovalActivity.validateTeamsConnection
        }
    })
    public teamsConnection?: MicrosoftTeamsLinkedService;

    @Metamodel.attribute({
        display: shellStringResources.ConnectionLabel,
        reference: true,
        type: LinkedService.MODEL,
        constraint: {
            validator: FabricApprovalActivity.validateWebConnection
        },
    })
    public webConnection?: LinkedService;

    @Metamodel.attribute({
        templatable: true,
        association: 'BODY',
        accepts: ['String']
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public body?: any;

    @Metamodel.attribute({
        display: shellStringResources.Team,
        association: 'TEAMID',
        templatable: true,
        constraint: {
            validator: FabricApprovalActivity.validateTeamId
        },
        accepts: ['String']
    })
    public teamId?: string;

    @Metamodel.attribute({
        display: shellStringResources.Channel,
        association: 'CHANNELID',
        templatable: true,
        constraint: {
            validator: FabricApprovalActivity.validateChannelId
        },
        accepts: ['String']
    })
    public channelId?: string;

    @Metamodel.attribute({
        display: shellStringResources.GroupChat,
        association: 'CHATID',
        templatable: true,
        constraint: {
            validator: FabricApprovalActivity.validateChatId
        },
        accepts: ['String']
    })
    public chatId?: string;

    @Metamodel.attribute({
        display: shellStringResources.OnBehalfOfUser_Label,
        association: 'UPN',
        templatable: true,
        accepts: ['String']
    })
    public upn?: string;

    public approvalType: ApprovalType = ApprovalType.Office365;

    public operationType: OperationType = OperationType.ApprovalMail;
    public teamsOperationType?: PostInType = PostInType.SendMessageInChannel;

    private static validateEmailConnection(_activity?: FabricApprovalActivity, connection?: LinkedService): IViolation[] | undefined {
        if (_activity && _activity.approvalType === ApprovalType.Office365) {
            if (_activity.operationType === OperationType.ApprovalMail && !connection) {
                return [
                    {
                        message: () => format(shellStringResources.RequiredConnectionTemplate,shellStringResources.Approval, shellStringResources.ConnectionLabel)
                    }
                ];
            }

            const connectionModel = Metamodel.modelOf(connection);
            if (connectionModel && connectionModel.name !== Office365EmailLinkedService.MODEL.name) {
                return [
                    {
                        message: () =>
                            format(
                                shellStringResources.ActivityNotSupportLinkedServiceError,
                                format(shellStringResources.ModelName_ActivityTemplate, shellStringResources.Approval),
                                format(shellStringResources.ModelName_ConnectionTemplate, connectionModel.name)
                            ),
                        messageType: 'unsupported'
                    }
                ];
            }
        }
        return undefined;
    }

    private static validateTeamsConnection(_activity?: FabricApprovalActivity, connection?: LinkedService): IViolation[] | undefined {
        if (_activity && _activity.approvalType === ApprovalType.MicrosoftTeams){
            if ((_activity.operationType === OperationType.ApprovalChannelMessage || _activity.operationType === OperationType.ApprovalGroupChatMessage) && !connection) {
                return [
                    {
                        message: () => format(shellStringResources.RequiredConnectionTemplate,shellStringResources.Approval, shellStringResources.ConnectionLabel)
                    }
                ];
            }

            const connectionModel = Metamodel.modelOf(connection);
            if (connectionModel && connectionModel.name !== MicrosoftTeamsLinkedService.MODEL.name) {
                return [
                    {
                        message: () =>
                            format(
                                shellStringResources.ActivityNotSupportLinkedServiceError,
                                format(shellStringResources.ModelName_ActivityTemplate, shellStringResources.Approval),
                                format(shellStringResources.ModelName_ConnectionTemplate, connectionModel.name)
                            ),
                        messageType: 'unsupported'
                    }
                ];
            }
        }
        return undefined;
    }

    private static validateWebConnection(_activity?: FabricApprovalActivity, connection?: LinkedService): IViolation[] | undefined {
        if (_activity && _activity.approvalType === ApprovalType.CustomEndpoint) {
            if (_activity.operationType === OperationType.ApprovalCustomHook && !connection) {
                return [
                    {
                        message: () => format(shellStringResources.RequiredConnectionTemplate,shellStringResources.Approval, shellStringResources.ConnectionLabel)
                    }
                ];
            }
        }
        return undefined;
    }

    private static validateTeamId(_activity?: FabricApprovalActivity, teamId?: string): IViolation[] | undefined {
        if (_activity && _activity.operationType === OperationType.ApprovalChannelMessage && _activity.teamsOperationType === PostInType.SendMessageInChannel && !teamId) {
            return [
                {
                    message: () => format(shellStringResources.RequiredTemplate, shellStringResources.Team)
                }
            ];
        }
        return undefined;
    }

    private static validateChannelId(_activity?: FabricApprovalActivity, channelId?: string): IViolation[] | undefined {
        if (_activity && _activity.operationType === OperationType.ApprovalChannelMessage && _activity.teamsOperationType === PostInType.SendMessageInChannel && !channelId) {
            return [
                {
                    message: () => format(shellStringResources.RequiredTemplate, shellStringResources.Channel)
                }
            ];
        }
        return undefined;
    }

    private static validateChatId(_activity?: FabricApprovalActivity, chatId?: string): IViolation[] | undefined {
        if (_activity && _activity.operationType === OperationType.ApprovalGroupChatMessage && _activity.teamsOperationType === PostInType.SendMessageInGroupChat && !chatId) {
            return [
                {
                    message: () => format(shellStringResources.RequiredTemplate, shellStringResources.GroupChat)
                }
            ];
        }
        return undefined;
    }

    private static validateApprovers(_activity?: FabricApprovalActivity, approvers?: string): IViolation[] | undefined {
        if (_activity && (_activity.approvalType === ApprovalType.MicrosoftTeams || _activity.approvalType === ApprovalType.Office365) && !approvers) {
            return [
                {
                    message: () => format(shellStringResources.RequiredTemplate, shellStringResources.ApproverLabel)
                }
            ];
        }
        return undefined;
    }

    private static validateApprovalRequestTitle(_activity?: FabricApprovalActivity, approvalRequestTitle?: string): IViolation[] | undefined {
        if (_activity && (_activity.approvalType === ApprovalType.MicrosoftTeams || _activity.approvalType === ApprovalType.Office365) && !approvalRequestTitle) {
            return [
                {
                    message: () => format(shellStringResources.RequiredTemplate, shellStringResources.RequestLabel)
                }
            ];
        }
        return undefined;
    }
}
